import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

const FREQ_LABELS = {
  daily:           { ar: 'يومي', en: 'Daily' },
  weekly:          { ar: 'أسبوعي', en: 'Weekly' },
  custom_weekdays: { ar: 'أيام محددة', en: 'Custom Days' },
  monthly:         { ar: 'شهري', en: 'Monthly' },
  one_time:        { ar: 'مرة واحدة', en: 'One Time' },
};

const STATUS_INFO = {
  active:    { ar: 'نشط', en: 'Active', bg: 'rgba(16,185,129,0.15)', color: '#6ee7b7' },
  paused:    { ar: 'متوقف', en: 'Paused', bg: 'rgba(245,158,11,0.15)', color: '#fcd34d' },
  expired:   { ar: 'منتهي', en: 'Expired', bg: 'rgba(100,116,139,0.2)', color: '#cbd5e1' },
  cancelled: { ar: 'ملغي', en: 'Cancelled', bg: 'rgba(239,68,68,0.15)', color: '#fca5a5' },
};

const VSMContractDetail = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const { id } = useParams();
  const navigate = useNavigate();

  const T = {
    backToList:    { ar: '← العودة للعقود', en: '← Back to Contracts' },
    contractDetails:{ ar: 'تفاصيل العقد', en: 'Contract Details' },
    contractTitle: { ar: 'العنوان', en: 'Title' },
    period:        { ar: 'الفترة', en: 'Period' },
    status:        { ar: 'الحالة', en: 'Status' },
    totalValue:    { ar: 'القيمة', en: 'Value' },
    notes:         { ar: 'ملاحظات', en: 'Notes' },
    clientsAndItems:{ ar: 'العملاء والبنود', en: 'Clients & Items' },
    summary:       { ar: 'الملخص', en: 'Summary' },
    totalClients:  { ar: 'إجمالي العملاء', en: 'Total Clients' },
    totalItems:    { ar: 'إجمالي البنود', en: 'Total Items' },
    plate:         { ar: 'اللوحة', en: 'Plate' },
    service:       { ar: 'الخدمة', en: 'Service' },
    frequency:     { ar: 'التكرار', en: 'Frequency' },
    time:          { ar: 'الوقت', en: 'Time' },
    price:         { ar: 'السعر', en: 'Price' },
    allVehicles:   { ar: 'كل السيارات (تلقائي)', en: 'All Vehicles (auto)' },
    loading:       { ar: 'جاري التحميل...', en: 'Loading...' },
    notFound:      { ar: 'العقد غير موجود', en: 'Contract not found' },
    cancel:        { ar: '🚫 إلغاء العقد', en: '🚫 Cancel Contract' },
    confirmCancel: { ar: 'هل أنت متأكد من إلغاء هذا العقد بالكامل؟', en: 'Cancel the entire contract?' },
    removeClient:  { ar: 'إزالة العميل من العقد', en: 'Remove client' },
    confirmRemove: { ar: 'إزالة هذا العميل من العقد؟', en: 'Remove this client from contract?' },
    untitled:      { ar: '(بدون عنوان)', en: '(untitled)' },
  };
  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchContract = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/contracts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setContract(data.data);
      else setContract(null);
    } catch (err) {
      console.error('[VSM] Fetch contract error:', err);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const handleCancel = async () => {
    if (!window.confirm(tr('confirmCancel'))) return;
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/contracts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      navigate('/vsm/contracts');
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveClient = async (clientId) => {
    if (!window.confirm(tr('confirmRemove'))) return;
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/contracts/${id}/clients/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchContract();
    } catch (err) {
      console.error(err);
    }
  };

  const formatFrequency = (item) => {
    const base = FREQ_LABELS[item.frequency]?.[lang] || item.frequency;
    if (item.frequency === 'monthly' && item.monthly_day) {
      return `${base} (${isRTL ? 'يوم' : 'day'} ${item.monthly_day})`;
    }
    return base;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString(isRTL ? 'ar-SA' : 'en-GB') : '—';

  if (loading) {
    return <Layout><div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{tr('loading')}</div></Layout>;
  }

  if (!contract) {
    return <Layout><div style={{ padding: '40px', textAlign: 'center', color: '#fca5a5' }}>{tr('notFound')}</div></Layout>;
  }

  const statusInfo = STATUS_INFO[contract.status] || STATUS_INFO.active;

  const sectionStyle = {
    background: 'rgba(15,23,42,0.5)', borderRadius: '12px',
    padding: '20px', marginBottom: '16px',
    border: '1px solid rgba(255,255,255,0.08)'
  };

  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '1200px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        {/* Back link */}
        <button
          onClick={() => navigate('/vsm/contracts')}
          style={{
            background: 'transparent', color: '#94a3b8', border: 'none',
            padding: '6px 0', fontSize: '13px', cursor: 'pointer',
            marginBottom: '12px'
          }}
        >{tr('backToList')}</button>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: '20px', flexWrap: 'wrap', gap: '16px'
        }}>
          <div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#f1f5f9', margin: 0, fontFamily: 'monospace' }} dir="ltr">
                {contract.contract_number}
              </h1>
              <span style={{
                background: statusInfo.bg, color: statusInfo.color,
                padding: '5px 12px', borderRadius: '12px',
                fontSize: '12px', fontWeight: '700'
              }}>
                {statusInfo[lang]}
              </span>
            </div>
            <p style={{ fontSize: '14px', color: '#cbd5e1', margin: '6px 0 0' }}>
              {contract.title || <span style={{ color: '#64748b', fontStyle: 'italic' }}>{tr('untitled')}</span>}
            </p>
          </div>
          {contract.status === 'active' && (
            <button
              onClick={handleCancel}
              style={{
                background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                border: '1px solid rgba(239,68,68,0.3)',
                padding: '8px 16px', borderRadius: '8px',
                fontSize: '13px', cursor: 'pointer', fontWeight: '600'
              }}
            >{tr('cancel')}</button>
          )}
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          {[
            { label: tr('period'), value: `${formatDate(contract.start_date)} → ${formatDate(contract.end_date)}`, icon: '📅', color: '#a5b4fc' },
            { label: tr('totalClients'), value: contract.clients?.length || 0, icon: '👥', color: '#6ee7b7' },
            { label: tr('totalItems'), value: contract.items_count || 0, icon: '📋', color: '#fb923c' },
            { label: tr('totalValue'), value: contract.total_value !== null ? `${parseFloat(contract.total_value).toFixed(2)} SAR` : '—', icon: '💰', color: '#fcd34d' },
          ].map((card, idx) => (
            <div key={idx} style={{
              background: 'rgba(15,23,42,0.5)', borderRadius: '10px',
              padding: '16px', border: '1px solid rgba(255,255,255,0.06)'
            }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>
                {card.icon} {card.label}
              </div>
              <div style={{ fontSize: '15px', color: card.color, fontWeight: '700' }}>
                {card.value}
              </div>
            </div>
          ))}
        </div>

        {contract.notes && (
          <div style={sectionStyle}>
            <h3 style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 8px', fontWeight: '600' }}>
              📝 {tr('notes')}
            </h3>
            <p style={{ color: '#cbd5e1', fontSize: '13px', margin: 0, whiteSpace: 'pre-wrap' }}>
              {contract.notes}
            </p>
          </div>
        )}

        {/* Clients & Items */}
        <h3 style={{
          fontSize: '16px', fontWeight: '700', color: '#f1f5f9',
          margin: '20px 0 12px', display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          👥 {tr('clientsAndItems')}
        </h3>

        {(contract.clients || []).map(clientGroup => (
          <div key={clientGroup.client_id} style={sectionStyle}>
            {/* Client header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '12px', paddingBottom: '12px',
              borderBottom: '1px solid rgba(255,255,255,0.06)'
            }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#a5b4fc' }}>
                  {isRTL && clientGroup.client_name_ar ? clientGroup.client_name_ar : clientGroup.client_name}
                </div>
                {clientGroup.client_phone && (
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }} dir="ltr">
                    📱 {clientGroup.client_phone}
                  </div>
                )}
              </div>
              {contract.status === 'active' && (
                <button
                  onClick={() => handleRemoveClient(clientGroup.client_id)}
                  title={tr('removeClient')}
                  style={{
                    background: 'rgba(239,68,68,0.1)', color: '#fca5a5',
                    border: '1px solid rgba(239,68,68,0.2)',
                    padding: '4px 10px', borderRadius: '6px',
                    fontSize: '11px', cursor: 'pointer', fontWeight: '600'
                  }}
                >✕</button>
              )}
            </div>

            {/* Items table */}
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <th style={{ padding: '8px 10px', textAlign: isRTL ? 'right' : 'left', color: '#94a3b8', fontWeight: '600' }}>{tr('plate')}</th>
                    <th style={{ padding: '8px 10px', textAlign: isRTL ? 'right' : 'left', color: '#94a3b8', fontWeight: '600' }}>{tr('service')}</th>
                    <th style={{ padding: '8px 10px', textAlign: isRTL ? 'right' : 'left', color: '#94a3b8', fontWeight: '600' }}>{tr('frequency')}</th>
                    <th style={{ padding: '8px 10px', textAlign: isRTL ? 'right' : 'left', color: '#94a3b8', fontWeight: '600' }}>{tr('time')}</th>
                    <th style={{ padding: '8px 10px', textAlign: isRTL ? 'right' : 'left', color: '#94a3b8', fontWeight: '600' }}>{tr('price')}</th>
                  </tr>
                </thead>
                <tbody>
                  {clientGroup.items.map(item => (
                    <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '10px', color: '#e2e8f0' }}>
                        {item.apply_to_all_vehicles ? (
                          <span style={{
                            background: 'rgba(16,185,129,0.15)', color: '#6ee7b7',
                            padding: '3px 8px', borderRadius: '6px',
                            fontSize: '11px', fontWeight: '600'
                          }}>
                            ✨ {tr('allVehicles')}
                          </span>
                        ) : (
                          <span style={{
                            background: 'linear-gradient(180deg, #fff, #e5e5e5)',
                            color: '#1a1a2e', padding: '3px 8px', borderRadius: '4px',
                            fontFamily: 'monospace', fontWeight: '700', fontSize: '11px'
                          }} dir="ltr">
                            {item.plate_letters_en} {item.plate_numbers}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px', color: '#e2e8f0' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px'
                        }}>
                          <span style={{
                            width: '10px', height: '10px', borderRadius: '50%',
                            background: item.service_color || '#6366f1', display: 'inline-block'
                          }} />
                          {isRTL ? item.service_name_ar : item.service_name}
                        </span>
                      </td>
                      <td style={{ padding: '10px', color: '#e2e8f0' }}>
                        {formatFrequency(item)}
                      </td>
                      <td style={{ padding: '10px', color: '#e2e8f0', fontFamily: 'monospace' }} dir="ltr">
                        {item.preferred_time ? item.preferred_time.slice(0, 5) : '—'}
                      </td>
                      <td style={{ padding: '10px', color: '#10b981', fontWeight: '600' }}>
                        {item.price_per_visit !== null ? `${parseFloat(item.price_per_visit).toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
};

export default VSMContractDetail;
